import React from 'react';
import PropTypes from 'prop-types';

import { t } from '../../../../services/i18nService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import styles from '../../testOverview.scss';

class PreCheckAction extends React.PureComponent {
    static propTypes = {
        className: PropTypes.string,
        current: PropTypes.bool,
        completed: PropTypes.bool,
        failed: PropTypes.bool,
        title: PropTypes.string.isRequired,
        children: PropTypes.node,
    };

    static defaultProps = {
        className: null,
        current: false,
        completed: false,
        failed: false,
    };

    componentDidUpdate(prevProps) {
        // Entering the failed state swaps the step content out, for instance "Grant access"
        // is replaced by "Try again" / "Exit". The browser drops focus to the body when the
        // focused button unmounts, leaving screen reader and keyboard users with no hint
        // that anything happened. Move focus to the title instead: the new state gets
        // announced and the replacement buttons follow it in the tab order.
        const hasJustFailed = this.props.failed && !prevProps.failed;

        if (hasJustFailed && this.isA11yV7Supported() && this.titleRef.current) {
            this.titleRef.current.focus();
        }
    }

    titleRef = React.createRef();

    isA11yV7Supported() {
        return a11yIIBScreenReaderService
            .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v7);
    }

    render() {
        const {
            completed,
            failed,
            title,
            current,
            children,
            className: propClassName,
        } = this.props;
        const isA11yV7Supported = this.isA11yV7Supported();
        let boxClassName = styles.actionBox;

        if (failed) {
            boxClassName += ` ${styles.componentFailed}`;
        }

        if (current) {
            boxClassName += ` ${styles.current}`;
        }

        // No need to check if it's a String, it's safe thanks to PropTypes
        if (propClassName) {
            boxClassName += ` ${propClassName}`;
        }

        return (
            // Deliberately not an alert region itself: focus moves to the title below and the
            // failure description carries its own role="alert", so announcing the whole box
            // as well would read the title and the description twice over.
            <div className={boxClassName}>
                <h4
                    className={styles.actionTitle}
                    ref={this.titleRef}
                    // Only focusable while failed, so the title stays out of the tab
                    // sequence during the normal flow.
                    {...(isA11yV7Supported && failed ? { tabIndex: -1 } : {})}
                >
                    {title}
                    {
                        // The check mark below is decorative, so the completed state is
                        // exposed to screen readers as part of the step title instead.
                        isA11yV7Supported && completed
                            ? <span className="sr-only">{`, ${t('testOverview.srOnly.stepCompleted')}`}</span>
                            : null
                    }
                </h4>
                {current ? children : null}
                {
                    completed ? (
                        <div className={styles.completed}>
                            <i className="fa fa-check" aria-hidden />
                        </div>
                    ) : null
                }
            </div>
        );
    }
}

export default PreCheckAction;
